import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import LoadingOverlay from "../components/reusable/LoadingOverlay";
import axios from "axios";
import Head from "next/head";
import Link from "next/link";
import Footer from "../components/Footer";

const Navbar = dynamic(() => import("../components/Navbar"), { ssr: false });

const SERVICES = [
  "AI for my business",
  "Software development",
  "Cybersecurity",
  "Cloud & infrastructure",
  "Data & analytics",
  "Automation & workflows",
  "IoT & connected devices",
  "Testing & quality assurance",
  "Not sure yet",
];

const COUNTRIES = [
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
  "India",
  "Singapore",
  "United Arab Emirates",
  "Saudi Arabia",
  "Qatar",
  "Bahrain",
  "Kuwait",
  "Afghanistan",
  "Albania",
  "Algeria",
  "Argentina",
  "Austria",
  "Bangladesh",
  "Belgium",
  "Brazil",
  "Chile",
  "China",
  "Colombia",
  "Czech Republic",
  "Denmark",
  "Egypt",
  "Ethiopia",
  "Finland",
  "France",
  "Germany",
  "Ghana",
  "Greece",
  "Hong Kong",
  "Hungary",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Japan",
  "Jordan",
  "Kenya",
  "Malaysia",
  "Mexico",
  "Morocco",
  "Netherlands",
  "New Zealand",
  "Nigeria",
  "Norway",
  "Oman",
  "Pakistan",
  "Philippines",
  "Poland",
  "Portugal",
  "Romania",
  "Russia",
  "South Africa",
  "South Korea",
  "Spain",
  "Sri Lanka",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Thailand",
  "Turkey",
  "Ukraine",
  "Vietnam",
  "Other",
];

const REQUIRED = ["fullName", "email", "phone", "country", "message"];

function validateField(name, value) {
  switch (name) {
    case "fullName":
      return value.trim() ? "" : "Please enter your name";
    case "email":
      if (!value.trim()) return "Email is required";
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Enter a valid email address";
    case "phone":
      if (!value.trim()) return "Phone number is required";
      return /^[+\d\s\-().]{7,}$/.test(value) ? "" : "Enter a valid phone number";
    case "country":
      return value ? "" : "Please select your country";
    case "message":
      return value.trim() ? "" : "Please describe your challenge";
    default:
      return "";
  }
}

const inputClass = (touched, error) =>
  `block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset sm:text-sm sm:leading-6 bg-gray-100 focus:outline-none focus:ring-2 ${
    touched && error ? "ring-red-400 focus:ring-red-400" : "ring-gray-300 focus:ring-[#59aec3]"
  }`;

export default function ContactUs() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    company: "",
    phone: "",
    country: "",
    message: "",
    services: [],
    consentPrivacy: false,
    consentMarketing: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (submitted) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [submitted]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setForm(prev => ({ ...prev, [name]: newValue }));
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, newValue) }));
    }
  };

  const handleBlur = e => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const toggleService = service => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service],
    }));
  };

  const isFormValid = REQUIRED.every(f => !validateField(f, form[f])) && form.consentPrivacy;

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitError("");

    const allTouched = REQUIRED.reduce((acc, f) => ({ ...acc, [f]: true }), {});
    const allErrors = REQUIRED.reduce((acc, f) => ({ ...acc, [f]: validateField(f, form[f]) }), {});
    setTouched(allTouched);
    setErrors(allErrors);
    if (Object.values(allErrors).some(Boolean)) return;

    setLoading(true);
    try {
      const nameParts = form.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      const extras = [
        form.country ? `Country: ${form.country}` : null,
        form.services.length ? `Interested in: ${form.services.join(", ")}` : null,
        form.consentMarketing ? "Marketing communications consent: Yes" : null,
      ]
        .filter(Boolean)
        .join("\n");

      const payload = {
        firstName,
        lastName,
        email: form.email,
        company: form.company,
        phone: form.phone,
        message: extras ? `${form.message}\n\n---\n${extras}` : form.message,
      };

      const res = await axios.post(
        "https://td00ruwir7.execute-api.ap-south-1.amazonaws.com/contactus",
        payload
      );
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "contact_form_submitted" });
      if (res.data == true) {
        setSubmitted(true);
      } else {
        setSubmitError("Couldn't send your message. Please try again.");
      }
    } catch {
      setSubmitError("Something went wrong. Please try again later.");
    }
    setLoading(false);
  };

  const STEPS = [
    {
      n: "1",
      title: "We review your message",
      desc: "Usually same day. Every submission is read by a person, not a bot.",
    },
    {
      n: "2",
      title: "We schedule a short call",
      desc: "30 minutes to understand your situation — no pitch deck, no sales script.",
    },
    {
      n: "3",
      title: "We send a clear recommendation",
      desc: "A specific proposal: what we'd do, why, and what it costs.",
    },
  ];

  return (
    <div>
      <Navbar />
      <Head>
        <title>Contact Us | ITMTB</title>
        <meta
          name="description"
          content="Get in touch with ITMTB for tailored technology and consulting solutions. We respond within 1 business day."
        />
        <meta
          name="keywords"
          content="contact ITMTB, IT consulting, digital transformation, cloud services, AI solutions, enterprise support"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Us | ITMTB" />
        <meta
          property="og:description"
          content="Connect with ITMTB to explore innovative tech solutions tailored to your business goals."
        />
        <meta property="og:image" content="https://www.itmtb.com/logo.png" />
        <meta property="og:url" content="https://www.itmtb.com/contact-us" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | ITMTB" />
        <meta
          name="twitter:description"
          content="Tell us what you're working on. We respond within 1 business day."
        />
        <meta name="twitter:image" content="https://www.itmtb.com/logo.png" />
        <link rel="canonical" href="https://www.itmtb.com/contact-us" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ContactPage",
              name: "Contact Us | ITMTB",
              description:
                "Connect with ITMTB to learn more about our services and how we can help your organisation.",
              url: "https://www.itmtb.com/contact-us",
              publisher: {
                "@type": "Organization",
                name: "ITMTB",
                logo: { "@type": "ImageObject", url: "https://www.itmtb.com/logo.png" },
              },
            }),
          }}
        />
      </Head>

      {/* Hero */}
      <div className="bg-[#59aec3] pt-16">
        <div className="w-[90%] md:w-[70%] mx-auto py-10 md:py-14">
          <h1 className="text-white font-semibold text-6xl">
            Start a conversation
          </h1>
        </div>
      </div>

      <div className="w-[90%] md:w-[70%] mx-auto max-w-[1800px]">
        <div className="pb-24 pt-10 sm:pb-32 sm:pt-14">
          {submitted ? (
            <div className="max-w-xl">
              {/* Confirmation header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#59aec3] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Message received</h2>
                  <p className="text-gray-500 text-sm mt-0.5">We'll get back to you within 1 business day.</p>
                </div>
              </div>

              {/* What happens next */}
              <div className="border-t border-gray-100 pt-8 mb-8">
                <h3 className="text-base font-semibold text-gray-900 mb-6">What happens next</h3>
                <ol className="space-y-6">
                  {STEPS.map(({ n, title, desc }) => (
                    <li key={n} className="flex gap-4">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#59aec3] text-white text-sm font-bold flex items-center justify-center mt-0.5">
                        {n}
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{title}</p>
                        <p className="text-gray-500 text-sm mt-1 leading-relaxed">{desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* While you wait */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-sm font-semibold text-gray-700 mb-1">While you wait</p>
                <p className="text-sm text-gray-500 mb-4">
                  See how we've delivered for companies with similar challenges.
                </p>
                <Link href="/success-stories">
                  <a className="inline-flex items-center text-sm font-semibold text-[#59aec3] hover:opacity-75 transition-opacity">
                    Read our success stories →
                  </a>
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-16 lg:gap-24">
              {/* ── Form ── */}
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                  Tell us what you're working on
                </h2>
                <p className="mt-2 text-md md:text-lg leading-8 text-gray-500">
                  We'll respond within 1 business day.
                </p>

                <form onSubmit={handleSubmit} noValidate className="mt-10 space-y-7">
                  {/* Service interest */}
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-3">
                      What do you need help with?{" "}
                      <span className="font-normal text-gray-400">Select all that apply</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {SERVICES.map(service => {
                        const selected = form.services.includes(service);
                        return (
                          <button
                            key={service}
                            type="button"
                            onClick={() => toggleService(service)}
                            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-150 cursor-pointer ${
                              selected
                                ? "bg-[#59aec3] border-[#59aec3] text-white"
                                : "bg-white border-gray-300 text-gray-700 hover:border-[#59aec3] hover:text-[#59aec3]"
                            }`}
                          >
                            {service}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Full name */}
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-semibold leading-6 text-gray-900"
                    >
                      Full name*
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="fullName"
                        id="fullName"
                        autoComplete="name"
                        value={form.fullName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={inputClass(touched.fullName, errors.fullName)}
                      />
                    </div>
                    {touched.fullName && errors.fullName && (
                      <p className="mt-1.5 text-sm text-red-500">{errors.fullName}</p>
                    )}
                  </div>

                  {/* Work email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold leading-6 text-gray-900"
                    >
                      Work email*
                    </label>
                    <div className="mt-2">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={inputClass(touched.email, errors.email)}
                      />
                    </div>
                    {touched.email && errors.email && (
                      <p className="mt-1.5 text-sm text-red-500">{errors.email}</p>
                    )}
                  </div>

                  {/* Company + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-semibold leading-6 text-gray-900"
                      >
                        Company
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="company"
                          id="company"
                          autoComplete="organization"
                          value={form.company}
                          onChange={handleChange}
                          className="block w-full rounded-md border-0 px-3.5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#59aec3]"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-semibold leading-6 text-gray-900"
                      >
                        Phone*
                      </label>
                      <div className="mt-2">
                        <input
                          type="tel"
                          name="phone"
                          id="phone"
                          autoComplete="tel"
                          value={form.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={inputClass(touched.phone, errors.phone)}
                        />
                      </div>
                      {touched.phone && errors.phone && (
                        <p className="mt-1.5 text-sm text-red-500">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <label
                      htmlFor="country"
                      className="block text-sm font-semibold leading-6 text-gray-900"
                    >
                      Country*
                    </label>
                    <div className="mt-2">
                      <select
                        id="country"
                        name="country"
                        value={form.country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={inputClass(touched.country, errors.country)}
                      >
                        <option value="">Select your country</option>
                        {COUNTRIES.map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                    {touched.country && errors.country && (
                      <p className="mt-1.5 text-sm text-red-500">{errors.country}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold leading-6 text-gray-900"
                    >
                      What's the challenge you're trying to solve?*
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        value={form.message}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={inputClass(touched.message, errors.message)}
                      />
                    </div>
                    {touched.message && errors.message && (
                      <p className="mt-1.5 text-sm text-red-500">{errors.message}</p>
                    )}
                  </div>

                  {/* Privacy consent — required */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consentPrivacy"
                      name="consentPrivacy"
                      checked={form.consentPrivacy}
                      onChange={handleChange}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#59aec3] focus:ring-[#59aec3] cursor-pointer"
                    />
                    <label htmlFor="consentPrivacy" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                      By providing my personal information, I agree to ITMTB&apos;s{" "}
                      {/* eslint-disable-next-line react/jsx-no-target-blank -- same-origin doc opened in new tab from form; noopener only, no need to strip own referrer */}
                      <a href="/privacy_policy.html" target="_blank" rel="noopener" className="underline text-[#59aec3] hover:text-[#3d8fa3]">
                        Terms of Use
                      </a>{" "}
                      and{" "}
                      {/* eslint-disable-next-line react/jsx-no-target-blank -- same-origin doc opened in new tab from form; noopener only, no need to strip own referrer */}
                      <a href="/privacy_policy.html" target="_blank" rel="noopener" className="underline text-[#59aec3] hover:text-[#3d8fa3]">
                        Privacy Policy
                      </a>
                      .
                    </label>
                  </div>

                  {/* Marketing consent — optional */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consentMarketing"
                      name="consentMarketing"
                      checked={form.consentMarketing}
                      onChange={handleChange}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#59aec3] focus:ring-[#59aec3] cursor-pointer"
                    />
                    <label htmlFor="consentMarketing" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                      I would like to receive marketing communications from ITMTB Technologies.
                    </label>
                  </div>

                  {submitError && <p className="text-sm text-red-500">{submitError}</p>}

                  <button
                    type="submit"
                    disabled={!isFormValid}
                    className={`rounded px-14 py-3 text-sm font-semibold text-white shadow-sm transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                      isFormValid
                        ? "bg-[#59aec3] hover:opacity-80 cursor-pointer"
                        : "bg-gray-300 cursor-not-allowed opacity-60"
                    }`}
                  >
                    Send message
                  </button>
                </form>
              </div>

              {/* ── Right panel ── */}
              <div className="hidden lg:block">
                <div className="sticky top-28 space-y-10">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-6">What happens next</h3>
                    <ol className="space-y-7">
                      {STEPS.map(({ n, title, desc }) => (
                        <li key={n} className="flex gap-4">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#59aec3] text-white text-sm font-bold flex items-center justify-center mt-0.5">
                            {n}
                          </span>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{title}</p>
                            <p className="text-gray-500 text-sm mt-1 leading-relaxed">{desc}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Who we work with</p>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      Companies in banking, telecom, supply chain, airlines, and consumer goods. If
                      your challenge is in or adjacent to those industries, we almost certainly have
                      relevant experience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading && <LoadingOverlay />}
      <Footer />
    </div>
  );
}
